import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ExpenseAPI, { Expense, ExpenseCategory, PaymentMode } from "../../services/expense.service";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  categories: ExpenseCategory[];
  onCategoryCreated: (category: ExpenseCategory) => void;
  editingExpense?: Expense | null;
}

const PAYMENT_MODES: { value: PaymentMode; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
  { value: "cheque", label: "Cheque" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "other", label: "Other" },
];

export const ExpenseEntryModal: React.FC<Props> = ({
  isOpen, onClose, onSuccess, categories, onCategoryCreated, editingExpense,
}) => {
  const [form, setForm] = useState({
    category_id: "", amount: "", expense_date: new Date().toISOString().slice(0, 10),
    vendor_name: "", payment_mode: "" as PaymentMode | "", payment_reference: "", notes: "",
  });
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      if (editingExpense) {
        setForm({
          category_id: editingExpense.category_id,
          amount: String(editingExpense.amount_paise / 100),
          expense_date: editingExpense.expense_date,
          vendor_name: editingExpense.vendor_name ?? "",
          payment_mode: editingExpense.payment_mode ?? "",
          payment_reference: editingExpense.payment_reference ?? "",
          notes: editingExpense.notes ?? "",
        });
      } else {
        setForm({
          category_id: "", amount: "", expense_date: new Date().toISOString().slice(0, 10),
          vendor_name: "", payment_mode: "", payment_reference: "", notes: "",
        });
      }
      setReceiptFile(null);
      setErrors({});
      setAddingCategory(false);
      setNewCategoryName("");
    }
  }, [isOpen, editingExpense]);

  if (!isOpen) return null;

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.category_id) e.category_id = "Category is required";
    const amountNum = parseFloat(form.amount);
    if (!form.amount || isNaN(amountNum) || amountNum <= 0) e.amount = "Enter a valid amount";
    if (!form.expense_date) e.expense_date = "Date is required";
    return e;
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const code = newCategoryName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const category = await ExpenseAPI.createCategory(newCategoryName.trim(), code);
      onCategoryCreated(category);
      setForm(f => ({ ...f, category_id: category.category_id }));
      setAddingCategory(false);
      setNewCategoryName("");
      toast.success("Category added");
    } catch (err: any) {
      toast.error(err?.message || "Failed to add category");
    }
  };

  const uploadReceipt = async (expenseId: string) => {
    if (!receiptFile) return;
    try {
      const { upload_url, s3_key } = await ExpenseAPI.getReceiptUploadUrl(expenseId);
      if (upload_url) {
        const res = await fetch(upload_url, {
          method: "PUT", body: receiptFile,
          headers: { "Content-Type": receiptFile.type },
        });
        if (!res.ok) throw new Error(`Receipt upload failed: HTTP ${res.status}`);
      }
      await ExpenseAPI.confirmReceipt(expenseId, s3_key);
    } catch (err: any) {
      toast.error(err?.message || "Receipt uploaded expense saved, but receipt upload failed");
    }
  };

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        category_id: form.category_id,
        amount_paise: Math.round(parseFloat(form.amount) * 100),
        expense_date: form.expense_date,
        vendor_name: form.vendor_name || undefined,
        payment_mode: form.payment_mode || undefined,
        payment_reference: form.payment_reference || undefined,
        notes: form.notes || undefined,
      };
      let expense: Expense;
      if (editingExpense) {
        expense = await ExpenseAPI.update(editingExpense.expense_id, payload);
      } else {
        expense = await ExpenseAPI.create(payload);
      }
      if (receiptFile) await uploadReceipt(expense.expense_id);
      toast.success(editingExpense ? "Expense updated" : "Expense recorded");
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err?.message || "Failed to save expense");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-slate-900">
            {editingExpense ? "Edit Expense" : "Record Expense"}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
            {!addingCategory ? (
              <div className="flex gap-2">
                <select
                  className={`flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.category_id ? "border-red-400" : "border-gray-200"}`}
                  value={form.category_id}
                  onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))}
                >
                  <option value="">Select category…</option>
                  {categories.map(c => (
                    <option key={c.category_id} value={c.category_id}>{c.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setAddingCategory(true)}
                  className="px-3 py-2 text-sm border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 whitespace-nowrap"
                >
                  + Add
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  autoFocus
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="New category name"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                />
                <button type="button" onClick={handleAddCategory} className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg">Save</button>
                <button type="button" onClick={() => setAddingCategory(false)} className="px-3 py-2 text-sm border border-gray-200 rounded-lg">Cancel</button>
              </div>
            )}
            {errors.category_id && <p className="text-red-500 text-xs mt-1">{errors.category_id}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Amount (₹)</label>
              <input
                type="number" step="0.01" min="0"
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.amount ? "border-red-400" : "border-gray-200"}`}
                value={form.amount}
                onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              />
              {errors.amount && <p className="text-red-500 text-xs mt-1">{errors.amount}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
              <input
                type="date"
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${errors.expense_date ? "border-red-400" : "border-gray-200"}`}
                value={form.expense_date}
                onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))}
              />
              {errors.expense_date && <p className="text-red-500 text-xs mt-1">{errors.expense_date}</p>}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Vendor name</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              value={form.vendor_name}
              onChange={e => setForm(f => ({ ...f, vendor_name: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment mode</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.payment_mode}
                onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value as PaymentMode }))}
              >
                <option value="">Select…</option>
                {PAYMENT_MODES.map(pm => <option key={pm.value} value={pm.value}>{pm.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Reference</label>
              <input
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={form.payment_reference}
                onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Receipt</label>
            {editingExpense?.receipt_s3_key && !receiptFile && (
              <div className="flex items-center gap-3 mb-2 text-sm">
                <span className="text-emerald-600">✓ Receipt already uploaded</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const { view_url } = await ExpenseAPI.getReceiptViewUrl(editingExpense.expense_id);
                      window.open(view_url, "_blank", "noopener,noreferrer");
                    } catch (err: any) {
                      toast.error(err?.message || "Failed to load receipt");
                    }
                  }}
                  className="text-blue-600 hover:underline"
                >
                  View
                </button>
              </div>
            )}
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={e => setReceiptFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-600 file:text-sm"
            />
            {editingExpense?.receipt_s3_key && (
              <p className="text-xs text-slate-400 mt-1">Choosing a new file will replace the existing receipt.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              rows={2}
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">
              {loading ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseEntryModal;
